import { prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Background pan/zoom for Full-Screen Hero + Text.
 *
 * Horizon’s desktop layout (≥990px) scrolls `.page-wrapper`, not the window.
 * Listening on window.scroll never fires there, so parallax looked broken.
 */
class EssParallaxHero extends HTMLElement {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {HTMLElement | null} */
  #mediaEl = null;

  #pan = 0;
  #zoom = 0;

  connectedCallback() {
    this.#pan = Number(this.dataset.pan || 0);
    this.#zoom = Number(this.dataset.zoom || 0);
    this.#mediaEl = this.querySelector('.ess-fullscreen-media-hero__media-el');

    if (prefersReducedMotion() || (!this.#pan && !this.#zoom) || !this.#mediaEl) return;

    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    window.addEventListener('resize', this.#update, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#unbindScrollTarget();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#update);
  }

  #bindScrollTarget = () => {
    this.#unbindScrollTarget();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#update, { passive: true });
  };

  #unbindScrollTarget = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#update);
    this.#scrollTarget = null;
  };

  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#update();
  };

  #update = () => {
    if (!this.#mediaEl) return;

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 1;

    if (rect.bottom < -vh || rect.top > vh * 2) return;

    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
    const y = (progress - 0.5) * 2 * this.#pan;
    const scale = 1 + progress * this.#zoom;
    this.#mediaEl.style.transform = `translateY(${y.toFixed(1)}px) scale(${scale.toFixed(3)})`;
  };
}

if (!customElements.get('ess-parallax-hero')) {
  customElements.define('ess-parallax-hero', EssParallaxHero);
}
